import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useMediaitemUpload } from '@hooks/useMediaitemUpload';
import { PlusIcon, XIcon } from '@icons';
import { MediaitemUsageType } from '@models';
import { BackgroundJobActivityTarget } from '@models/backgroundActivities';
import { setupForDark } from '@theme';
import Button from './Button';
import InputErrorMessage from './InputErrorMessage';

const MediaitemInput = ({
  target,
  placeholder,
  allowedFileTypes,
  maxFileSize,
  mediaitemUsageType,
  className,
  onUploaded
}: {
  target: BackgroundJobActivityTarget;
  placeholder?: string;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  mediaitemUsageType: MediaitemUsageType;
  className?: string;
  onUploaded: (mediaitemId: number, originalFilename?: string) => void;
}) => {
  const { t } = useTranslation();
  const {
    inputId,
    fileInputRef,
    onChange,
    uploadProgress,
    error,
    acceptString,
    cancel,
    isUploading
  } = useMediaitemUpload({
    target,
    allowedFileTypes,
    maxFileSize,
    mediaitemUsageType,
    onUploaded
  });

  return (
    <div className={className}>
      <input
        className='hidden'
        type='file'
        ref={fileInputRef}
        multiple={false}
        onChange={onChange}
        accept={acceptString}
        id={inputId}
        disabled={isUploading}
      />
      <div className={classNames('flex flex-row items-center justify-start gap-xs py-xs px-xxs')}>
        <Button labelFor={inputId} fitContent={true} onClick={() => {}} type='textual' size='small'>
          <div className='P4B flex flex-row items-center justify-center gap-xxs'>
            <PlusIcon size={20} />
            <div>{placeholder}</div>
          </div>
        </Button>
        {isUploading && mediaitemUsageType !== MediaitemUsageType.MENTOR_ATTACHMENT && (
          <div
            className={classNames(
              'flex flex-row items-center gap-xxs P4B',
              setupForDark('text-tertiary')
            )}
          >
            <span>{t('components.fileInput.progress', { progress: uploadProgress ?? 0 })}</span>
            <button
              type='button'
              onClick={cancel}
              aria-label={t('components.fileInput.cancel')}
              className='p-xxs rounded-full cursor-pointer'
            >
              <XIcon size={14} />
            </button>
          </div>
        )}
      </div>
      {error && <InputErrorMessage error={error} />}
    </div>
  );
};

export default MediaitemInput;
